import * as React from 'react';
import { Field, formValueSelector } from 'redux-form';
import { connect } from 'react-redux';
import { flow, forEachRight, map } from 'lodash';
import { II18nMessages } from '../../../../../../services/branding/models';
import { Button, Col, ControlLabel, FormGroup, Row } from 'react-bootstrap';
import FieldFormControl from '../../../../../../components/FieldFormControl';
import SelectControl from '../../../../../../components/SelectControl';
import { FaTrash } from 'react-icons/fa';
import GroupTypeIcon from '../GroupTypeIcon';
import * as uuid from 'uuid/v4';
import { isRequired, isEmail, isPhone, isAdult, isValidDate } from '../../../../../../util/validators';
import { IProduct } from '../../../../../../data/api/models';

type TravellerType = 'child' | 'adult';

interface ITravellerTypeConfig {
  min: number;
  max: number;
}

interface IGroupType {
  adult: ITravellerTypeConfig;
  child?: ITravellerTypeConfig;
}

interface IGroupTypeMap {
  [type: string]: IGroupType;
}

interface ITripDetailsForm {
  i18n: II18nMessages;
  pristine: boolean;
  fields: any;
  groupType: string;
  tripType: string;
  renderDefinition(name: string): any;
  product: IProduct;
}

const groupTypeTravellerMap: IGroupTypeMap = {
  individual: {
    adult: {
      min: 1,
      max: 1,
    },
    child: {
      min: 0,
      max: 0,
    },
  },
  couple: {
    adult: {
      min: 2,
      max: 2,
    },
    child: {
      min: 0,
      max: 0,
    },
  },
  family: {
    adult: {
      min: 1,
      max: 2,
    },
    child: {
      min: 1,
      max: 4,
    },
  },
  group: {
    adult: {
      min: 1,
      max: 10,
    },
    child: {
      min: 0,
      max: 0,
    },
  },
};

const defaultTraveller = {
  id: '',
  title: '',
  firstName: '',
  lastName: '',
  dob: '',
  phone: '',
  email: '',
  type: '',
  screening: null,
  screeningSessionId: null,
};

const childTitles = [
    { label: 'Master', value: 'Master' },
    { label: 'Miss', value: 'Miss' },
    { label: 'Mr', value: 'Mr' },
];

const adultTitles = [
    { label: 'Mr', value: 'Mr' },
    { label: 'Mrs', value: 'Mrs' },
    { label: 'Miss', value: 'Miss' },
    { label: 'Ms', value: 'Ms' },
    { label: 'Master', value: 'Master' },
    { label: 'Dr', value: 'Dr' },
    { label: 'Prof', value: 'Prof' },
    { label: 'Rev', value: 'Rev' },
    { label: 'Cpt', value: 'Cpt' },
    { label: 'Sir', value: 'Sir' } ,
    { label: 'Dame', value: 'Dame' },
    { label: 'Lord', value: 'Lord' },
    { label: 'Lady', value: 'Lady' },
    { label: 'Rt Honourable', value: 'Rt Honourable' },
];

class Travellers extends React.Component<ITripDetailsForm> {
  private getAll = (type: TravellerType, fields) => fields.getAll().filter((traveller) => traveller.type === type);
  private count = (type: TravellerType, fields) => this.getAll(type, fields).length;
  private handleAddField = (type: TravellerType, fields) => () => this.addField(type, fields);
  private handleRemoveField = (id, fields) => () => this.removeField(id, fields);

  private getGroupTypeTravellerMap = (groupType) => {
    const config = groupTypeTravellerMap[groupType];
    const { product, tripType } = this.props;

    // Allow 6 children for family on SALTOM & SALTOMLS
    const productsAllowingSixChildren = ['SALTOM', 'SALTOMLS'];
    if (productsAllowingSixChildren.includes(product.productCode) && groupType === 'family') {
      config.child.max = 6;
    }

    if (groupType !== 'group' || tripType !== 'single') {
      return config;
    }

    // Products where to enable adding children to group travellers
    //
    // Salt ST
    // Salt OM ST
    // Axiom Broker Network ST
    // Axiom Broker Network - Euro - ST
    // Groupsure ST
    // Groupsure ex-med ST
    // Lloyds Broker Network ST

    const products = ['SALT', 'SALTOM', 'ETIU', 'ETIE', 'ETIEG', 'ETIEES', 'LLOR'];
    config.child.max = products.includes(product.productCode) ? 5 : 0;

    return config;
  }

  private handleTravellerUpdates = (type: TravellerType, config: ITravellerTypeConfig, fields) => {
    let total = this.count(type, fields);

    if (total > config.max) {
      // Remove travellers from the form until the maximum amount of travellers are
      // achieved for the next group type.

      forEachRight(this.getAll(type, fields), (traveller) => {
        if (total <= config.max) {
          return;
        }

        this.removeField(traveller.id, fields);
        total -= 1;
      });
    }

    if (total < config.min) {
      const totalToAdd = config.min - total;

      for (let i = 0; i < totalToAdd; i++) {
        this.addField(type, fields);
      }
    }
  }

  public componentWillReceiveProps(nextProps: ITripDetailsForm) {
    if (this.props.groupType === nextProps.groupType) {
      return;
    }

    const { fields, groupType } = nextProps;
    const config = this.getGroupTypeTravellerMap(groupType ? groupType : 'individual');

    if (config) {
      const { adult, child } = config;

      this.handleTravellerUpdates('adult', adult, fields);
      this.handleTravellerUpdates('child', child, fields);
    }
  }

  private addField = (type: TravellerType, fields) => {
    fields.push({
      ...defaultTraveller,
      id: uuid(),
      type,
    });
  }

  private getIndex = (id: string, fields) => {
    let travellerIndex = -1;
    const travellers = fields.getAll();

    travellers.forEach((traveller, index) => {
      if (traveller.id === id) {
        travellerIndex = index;
      }
    });

    if (travellerIndex === -1) {
      console.error('Couldn\'t find traveller', travellers, id, travellerIndex);
    }

    return travellerIndex;
  }

  private removeField = (id: string, fields) => {
    const travellerIndex = this.getIndex(id, fields);

    if (travellerIndex === -1) {
      return;
    }

    fields.remove(travellerIndex);
  }

  private renderLeadTravellerFields = (traveller, fieldIndex) => {
    const { i18n, product, branding, renderDefinition } = this.props;

    const residencies = map(product.metadata.residencies, (item, key) => ({
      label: item,
      value: key,
    }));

    return (
      <div>
        <Col sm={12}>
          <FormGroup controlId="residency">
            <Col componentClass={ControlLabel} sm={3}>
              {i18n.quote.tripDetails.residency}
            </Col>
            <Col sm={6}>
              <Field
                name={`residency`}
                type="select"
                options={residencies}
                component={SelectControl}
                clearable={false}
                validate={[isRequired]}
              />
            </Col>
          </FormGroup>
        </Col>
        {(branding.channel.key === 'salt-om-ls-b2c' || branding.channel.key === 'salt-om-b2c') && (
          <Col sm={12}>
            <FormGroup controlId="sending-field">
              {renderDefinition('sending_field')}
              <Col componentClass={ControlLabel} sm={3}>
                {i18n.quote.tripDetails.sendingField}
              </Col>
              <Col sm={6}>
                <Field
                  name={`sendingField`}
                  type="text"
                  component={FieldFormControl}
                  validate={[
                    isRequired,
                  ]}
                />
              </Col>
            </FormGroup>
          </Col>
        )}
        <Col sm={12}>
          <FormGroup controlId="phone">
            <Col componentClass={ControlLabel} sm={3}>
              {i18n.quote.tripDetails.traveller.phone}
            </Col>
            <Col sm={6}>
              <Field
                name={`travellers[${fieldIndex}].phone`}
                type="text"
                component={FieldFormControl}
                validate={[isRequired, isPhone]}
              />
            </Col>
          </FormGroup>
        </Col>
        <Col sm={12}>
          <FormGroup controlId="email">
            <Col componentClass={ControlLabel} sm={3}>
              {i18n.quote.tripDetails.traveller.email}
            </Col>
            <Col sm={6}>
              <Field
                name={`travellers[${fieldIndex}].email`}
                type="email"
                component={FieldFormControl}
                validate={[isRequired, isEmail]}
              />
            </Col>
          </FormGroup>
        </Col>
      </div>
    );
  }

  private renderFields(type: TravellerType) {
    const { fields, i18n, groupType } = this.props;
    const config = this.getGroupTypeTravellerMap(groupType ? groupType : 'individual');
    const travellerConfig = config[type];

    return this.getAll(type, fields).map((traveller, index) => {
      const fieldIndex = this.getIndex(traveller.id, fields);
      const dobValidator = type === 'adult' ? isAdult : () => {/*noop*/};

      return (
        <Row className="margin-top" key={index}>
          <Col sm={12}>
            <Col sm={12} className="traveller">
              {index === 0 && type === 'adult' && (<h5>{i18n.quote.tripDetails.traveller.lead}</h5>)}
              {(index > 0 || type !== 'adult') && (<h5>{i18n.quote.tripDetails.traveller[type]} {index + 1}</h5>)}
              <div className="trash-btn">
                <Button
                  bsStyle="default"
                  onClick={this.handleRemoveField(traveller.id, fields)}
                  className={index <= (travellerConfig.min - 1) ? 'hidden' : ''}
                >
                  <FaTrash />
                </Button>
              </div>
              <Row>
                <Col sm={12}>
                  <FormGroup controlId="name">
                    <Col componentClass={ControlLabel} sm={3}>
                      {i18n.quote.tripDetails.traveller.name}
                    </Col>
                    <Col sm={2}>
                      <Field
                        name={`travellers[${fieldIndex}].title`}
                        type="text"
                        placeholder="Title"
                        options={type === 'adult' ? adultTitles : childTitles}
                        component={SelectControl}
                        clearable={false}
                        validate={[isRequired]}
                      />
                    </Col>
                    <Col sm={4}>
                      <Field
                        name={`travellers[${fieldIndex}].firstName`}
                        type="text"
                        placeholder="First Name"
                        component={FieldFormControl}
                        validate={[isRequired]}
                      />
                    </Col>
                    <Col sm={3}>
                      <Field
                        name={`travellers[${fieldIndex}].lastName`}
                        type="text"
                        placeholder="Last Name"
                        component={FieldFormControl}
                        validate={[isRequired]}
                      />
                    </Col>
                  </FormGroup>
                </Col>
                <Col sm={12}>
                  <FormGroup controlId="dob">
                    <Col componentClass={ControlLabel} sm={3}>
                      {i18n.quote.tripDetails.traveller.dob}
                    </Col>
                    <Col sm={4}>
                      <Field
                        name={`travellers[${fieldIndex}].dob`}
                        type="masked-date"
                        component={FieldFormControl}
                        placeholder="dd/mm/yyyy"
                        className="form-control"
                        validate={[isRequired, isValidDate, dobValidator]}
                      />
                    </Col>
                  </FormGroup>
                </Col>
                {index === 0 && type === 'adult' && this.renderLeadTravellerFields(traveller, fieldIndex)}
              </Row>
            </Col>
          </Col>
        </Row>
      );
    });
  }

  public componentWillMount() {
    const { fields } = this.props;
    const fieldLength = fields.length;

    if (fieldLength === 0) {
      this.addField('adult', fields);
    }
  }

  public render() {
    const { i18n, fields, groupType, renderDefinition, product, tripType } = this.props;
    const config = this.getGroupTypeTravellerMap(groupType ? groupType : 'individual');

    return (
      <div>
        {i18n.quote.tripDetails.travellers} {renderDefinition('group_type')}
        <Row className="radio-group margin-top">
          {map(product.metadata[`group_type_trip_${tripType}`], (name, key) => {
            return (
              <Col xs={6} md={3} key={key}>
                <Field
                  name="groupType"
                  type="radio"
                  value={key}
                  component={FieldFormControl}
                  validate={[isRequired]}
                >
                  <GroupTypeIcon type={key} />
                  {name}
                </Field>
              </Col>
            );
          })}
        </Row>

        <div className="section">
          <div className="margin-bottom">
            {this.renderFields('adult')}
          </div>
          <Button
            bsStyle="primary"
            onClick={this.handleAddField('adult', fields)}
            className={this.count('adult', fields) >= config.adult.max ? 'hidden' : ''}
          >
            {i18n.quote.button.adult}
          </Button>
        </div>

        <div className={config.child.max === 0 ? 'hidden' : 'section'}>
          <div className="margin-bottom">
            {this.renderFields('child')}
          </div>
          <Button
            bsStyle="primary"
            onClick={this.handleAddField('child', fields)}
            className={this.count('child', fields) >= config.child.max ? 'hidden' : ''}
          >
            {i18n.quote.button.child}
          </Button>
        </div>
      </div>
    );
  }
}

let selector;

export default flow([
  (component) => {
    selector = formValueSelector('quote');

    return component;
  },
  connect(
    (state) => {
      const groupType = selector(state, 'groupType');
      const tripType = selector(state, 'tripType');

      return {
        groupType,
        tripType,
        branding: state.branding,
      };
    },
  ),
])(Travellers);
